import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { adminAPI, usersAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import FilterSelect from '../../components/common/FilterSelect';

const FEATURE_OPTIONS = [
  { key: 'tasks.board', label: 'Tasks Board', description: 'Show the personal tasks page in navigation.' },
  { key: 'logistics.tracker', label: 'Logistic Report', description: 'Allow opening Logistic Tracker page (view only).' },
  { key: 'logistics.manage', label: 'Logistic Desk', description: 'Allow creating and updating logistics orders for any role with this grant.' },
  { key: 'logistics.desk', label: 'Logistic Desk (Legacy Alias)', description: 'Compatibility alias mapped to logistics tracker/manage access checks.' },
  { key: 'dairy_order.task', label: 'Dairy Order Task', description: 'Allow entering daily dairy inward/outward records.' },
  { key: 'dairy_order.report', label: 'Dairy Order Report', description: 'Allow viewing dairy order report.' },
  { key: 'goods_received.task', label: 'Goods Received Task', description: 'Allow entering goods received quantities.' },
  { key: 'goods_received.report', label: 'Goods Received Report', description: 'Allow viewing goods received report.' },
  { key: 'product_returns.task', label: 'Product Returns Task', description: 'Allow creating product return entries.' },
  { key: 'product_returns.report', label: 'Product Returns Report', description: 'Allow viewing product returns report.' },
  { key: 'item_change_log.page', label: 'Item Change Log', description: 'Allow reviewing and escalating prefilled item change log rows.' },
  { key: 'produce_tracker.task', label: 'Produce Tracker Task', description: 'Allow using produce inventory tracker task page.' },
  { key: 'produce_tracker.report', label: 'Produce Tracker Report', description: 'Allow viewing produce tracker report.' },
  { key: 'inventory_count.task', label: 'Inventory Count Task', description: 'Allow completing full inventory count task.' },
  { key: 'inventory_count.report', label: 'Inventory Count Report', description: 'Allow viewing full inventory report.' },
  { key: 'daily_liquor_count.task', label: 'Daily Liquor Count Task', description: 'Allow entering daily liquor count values.' },
  { key: 'daily_liquor_count.report', label: 'Daily Liquor Count Report', description: 'Allow viewing daily liquor count report tab.' },
  { key: 'final_report.view', label: 'Final Report', description: 'Allow viewing final report tab.' },
  { key: 'inventory_count.manage', label: 'Inventory Count Schedule', description: 'Allow scheduling full inventory count days.' },
  { key: 'workforce_roster.view', label: 'Roster View', description: 'Allow opening workforce roster page.' },
  { key: 'workforce_roster.manage', label: 'Roster Manage', description: 'Allow editing and syncing workforce roster.' },
  { key: 'reports.hub', label: 'Reports Hub', description: 'Show reports section and enable report tabs.' },
  { key: 'reports.all_stores', label: 'Reports All Stores', description: 'Allow filtering reports across all stores.' },
  { key: 'bi.hub', label: 'BI Portal', description: 'Show Business Intelligence portal in navigation.' },
  { key: 'bi.budget', label: 'BI Budget vs Actuals', description: 'Allow BI Budget Target vs Achieved and Run Rate tab.' },
  { key: 'bi.overview', label: 'BI Overview', description: 'Allow BI executive operations & sales dashboard tab.' },
  { key: 'bi.funnel', label: 'BI Funnel', description: 'Allow BI order funnel analysis tab.' },
  { key: 'bi.promo', label: 'Promo Deals', description: 'Allow Promo Deals (Features/Offers) tab.' },
  { key: 'bi.calendar', label: 'BI Calendar', description: 'Allow BI task scheduling calendar tab.' },
  { key: 'bi.ba_report', label: 'BI BA Report', description: 'Allow BI business analytics report generator tab.' },
  { key: 'inventory.daily', label: 'Daily Inventory', description: 'Allow access to daily inventory page.' },
  { key: 'products.sync', label: 'Product Snapshot', description: 'Allow product snapshot/status endpoints.' },
  { key: 'stores.manage', label: 'Store Management', description: 'Allow creating stores from user management.' },
  { key: 'dashboard.admin_overview', label: 'Dashboard Admin Overview', description: 'Allow dashboard admin overview widgets.' },
  { key: 'store_close.close', label: 'Store Close', description: 'Allow closing the store shutter and requesting reopen with a reason.' },
  { key: 'store_close.edit', label: 'Store Close Edit / Reopen', description: 'Allow admin to review reopen requests, edit sales, and reopen the shutter.' },
  { key: 'notifications.admin', label: 'Notice Board Admin', description: 'Allow board summary and broadcast notices.' },
  { key: 'users.manage', label: 'User Management', description: 'Allow opening user management page.' },
  { key: 'admin.feature_access', label: 'Feature Access Admin', description: 'Allow opening this feature access management page.' },
  { key: 'admin.category_classifications', label: 'Category Classification Admin', description: 'Allow mapping Foodmandu categories to operational task groups.' },
  { key: 'admin.assets', label: 'Store Assets Admin', description: 'Allow managing store assets used by temperature checks.' },
];

const normalizePermissionList = (permissions) => (Array.isArray(permissions) ? permissions : []);

const getRoleFeatureActions = (role) => {
  const permissions = normalizePermissionList(role?.permissions);
  const featurePermission = permissions.find(
    (entry) => String(entry?.module || '').trim().toLowerCase() === 'feature_access'
  );
  const actions = Array.isArray(featurePermission?.actions) ? featurePermission.actions : [];
  return Array.from(new Set(actions.map((action) => String(action).trim()).filter(Boolean)));
};

const buildRolePermissionsWithFeatureAccess = (permissions, actions) => {
  const nonFeaturePermissions = normalizePermissionList(permissions).filter(
    (entry) => String(entry?.module || '').trim().toLowerCase() !== 'feature_access'
  );

  return [
    ...nonFeaturePermissions,
    {
      module: 'feature_access',
      actions: Array.from(new Set(actions.map((action) => String(action).trim()).filter(Boolean))),
    },
  ];
};

const getUserRoleLabel = (user) => user?.roles?.name || user?.role || '-';

const getUserFeatureConfig = (user) => {
  const metrics = user?.performance_metrics || user?.performanceMetrics || {};
  const config = metrics?.featureAccess || metrics?.feature_access || user?.featureAccess || user?.feature_access;

  if (!config) return { mode: 'inherit', actions: [] };
  if (Array.isArray(config)) return { mode: 'custom', actions: config };

  if (typeof config === 'object' && !Array.isArray(config)) {
    const mode = String(config.mode || '').trim().toLowerCase() === 'inherit' ? 'inherit' : 'custom';
    const actions = Array.isArray(config.actions) ? config.actions : [];
    return { mode, actions };
  }

  return { mode: 'inherit', actions: [] };
};

const FeatureAccessManagement = () => {
  const { hasFeatureAccess } = useAuthStore();
  const canManageFeatureAccess = hasFeatureAccess('admin.feature_access');

  const [loading, setLoading] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [savingUser, setSavingUser] = useState(false);

  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchPerformed, setUserSearchPerformed] = useState(false);
  const [userResultCount, setUserResultCount] = useState(0);

  const [scope, setScope] = useState('role');

  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [roleActions, setRoleActions] = useState([]);

  const [selectedUserId, setSelectedUserId] = useState('');
  const [userMode, setUserMode] = useState('inherit');
  const [userActions, setUserActions] = useState([]);

  const selectedRole = useMemo(
    () => roles.find((entry) => String(entry.id) === String(selectedRoleId)) || null,
    [roles, selectedRoleId]
  );

  const selectedUser = useMemo(
    () => users.find((entry) => String(entry.id) === String(selectedUserId)) || null,
    [users, selectedUserId]
  );

  const roleHasAll = useMemo(
    () => roleActions.includes('all') || roleActions.includes('*'),
    [roleActions]
  );
  const userHasAll = useMemo(
    () => userActions.includes('all') || userActions.includes('*'),
    [userActions]
  );

  const isRoleScope = scope === 'role';
  const hasSelectedUser = Boolean(selectedUser);
  const savingCurrent = isRoleScope ? savingRole : savingUser;
  const activeActions = isRoleScope ? roleActions : userActions;
  const activeHasAll = isRoleScope ? roleHasAll : userHasAll;
  const canEditFeatureGrid = isRoleScope || (hasSelectedUser && userMode === 'custom');
  const scopeLabel = isRoleScope ? 'Role Defaults' : 'User Access';
  const scopeOptions = useMemo(() => ([
    { value: 'role', label: 'Role Defaults' },
    { value: 'user', label: 'User Override' },
  ]), []);
  const roleSelectOptions = useMemo(() => {
    if (roles.length === 0) {
      return [{ value: '', label: loading ? 'Loading roles...' : 'No roles found' }];
    }
    return roles.map((entry) => ({ value: String(entry.id), label: entry.name }));
  }, [loading, roles]);
  const userSelectOptions = useMemo(() => {
    if (!userSearchPerformed) {
      return [{ value: '', label: 'Search users first' }];
    }
    if (users.length === 0) {
      return [{ value: '', label: userSearchLoading ? 'Searching users...' : 'No users found' }];
    }
    return users.map((entry) => ({
      value: String(entry.id),
      label: `${entry.name} (${entry.email})`,
    }));
  }, [userSearchLoading, userSearchPerformed, users]);

  useEffect(() => {
    if (!canManageFeatureAccess) return;

    const loadRoles = async () => {
      setLoading(true);
      try {
        const rolesRes = await adminAPI.getRoles();

        const nextRoles = rolesRes.data?.data || [];
        setRoles(nextRoles);

        if (nextRoles.length > 0) {
          setSelectedRoleId((current) => current || String(nextRoles[0].id));
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load role access data');
      } finally {
        setLoading(false);
      }
    };

    loadRoles();
  }, [canManageFeatureAccess]);

  useEffect(() => {
    if (!selectedRole) {
      setRoleActions([]);
      return;
    }
    setRoleActions(getRoleFeatureActions(selectedRole));
  }, [selectedRole]);

  useEffect(() => {
    if (!selectedUser) {
      setUserMode('inherit');
      setUserActions([]);
      return;
    }

    const featureConfig = getUserFeatureConfig(selectedUser);
    setUserMode(featureConfig.mode);
    setUserActions(Array.from(new Set(featureConfig.actions.map((action) => String(action).trim()).filter(Boolean))));
  }, [selectedUser]);

  const toggleCurrentAction = (action) => {
    if (isRoleScope) {
      setRoleActions((current) => {
        const next = new Set(current);
        if (next.has(action)) next.delete(action);
        else next.add(action);
        return Array.from(next);
      });
      return;
    }

    setUserActions((current) => {
      const next = new Set(current);
      if (next.has(action)) next.delete(action);
      else next.add(action);
      return Array.from(next);
    });
  };

  const searchUsers = async () => {
    const query = userSearch.trim();
    if (query.length < 2) {
      toast.error('Enter at least 2 characters to search users');
      return;
    }

    setUserSearchLoading(true);
    setUserSearchPerformed(true);
    setSelectedUserId('');
    setUsers([]);

    try {
      const response = await usersAPI.getAll({
        search: query,
        page: 1,
        limit: 25,
      });
      const nextUsers = response.data?.data || [];
      setUsers(nextUsers);
      setUserResultCount(response.data?.pagination?.total || nextUsers.length);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to search users');
    } finally {
      setUserSearchLoading(false);
    }
  };

  const saveRoleDefaults = async () => {
    if (!selectedRole) return;
    setSavingRole(true);
    try {
      const nextPermissions = buildRolePermissionsWithFeatureAccess(selectedRole.permissions, roleActions);
      const response = await adminAPI.updateRolePermissions(selectedRole.id, nextPermissions);
      const updatedRole = response.data?.data || {};

      setRoles((current) => current.map((entry) => (
        String(entry.id) === String(updatedRole.id) ? { ...entry, ...updatedRole } : entry
      )));

      toast.success(`Default role access updated for ${selectedRole.name}`);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save role defaults');
    } finally {
      setSavingRole(false);
    }
  };

  const saveUserOverride = async () => {
    if (!selectedUser) return;
    setSavingUser(true);
    try {
      const nextActions = Array.from(new Set(userActions.map((action) => String(action).trim()).filter(Boolean)));
      await adminAPI.updateUserFeatureAccess(selectedUser.id, {
        mode: userMode,
        actions: userMode === 'custom' ? nextActions : [],
      });

      setUsers((current) => current.map((entry) => {
        if (String(entry.id) !== String(selectedUser.id)) return entry;

        const currentMetrics = (
          entry.performance_metrics
          && typeof entry.performance_metrics === 'object'
          && !Array.isArray(entry.performance_metrics)
        ) ? { ...entry.performance_metrics } : {};

        if (userMode === 'inherit') {
          delete currentMetrics.featureAccess;
          delete currentMetrics.feature_access;
        } else {
          currentMetrics.featureAccess = { mode: 'custom', actions: nextActions };
          currentMetrics.feature_access = { mode: 'custom', actions: nextActions };
        }

        return {
          ...entry,
          performance_metrics: currentMetrics,
        };
      }));

      toast.success(`${selectedUser.name} now uses ${userMode === 'inherit' ? 'role defaults' : 'custom user access'}`);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save user access');
    } finally {
      setSavingUser(false);
    }
  };

  const handleSave = () => {
    if (isRoleScope) {
      saveRoleDefaults();
      return;
    }
    saveUserOverride();
  };

  if (!canManageFeatureAccess) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Feature Access</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Feature Access</h1>
      </div>

      <div className="card space-y-4">
        <div className={`grid grid-cols-1 gap-4 ${
          isRoleScope ? 'lg:grid-cols-[220px_320px_1fr_auto]' : 'lg:grid-cols-[220px_minmax(320px,1fr)_320px_auto]'
        }`}>
          <div>
            <FilterSelect
              label="Scope"
              className="w-full"
              value={scope}
              options={scopeOptions}
              onChange={(nextScope) => setScope(String(nextScope))}
              disabled={loading || savingCurrent || userSearchLoading}
            />
          </div>

          <div>
            {isRoleScope ? (
              <FilterSelect
                label="Role"
                className="w-full"
                value={selectedRoleId || roleSelectOptions[0]?.value || ''}
                options={roleSelectOptions}
                onChange={(nextValue) => setSelectedRoleId(String(nextValue))}
                disabled={loading || savingCurrent}
              />
            ) : (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Search User</label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    className="input"
                    value={userSearch}
                    onChange={(event) => setUserSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') searchUsers();
                    }}
                    placeholder="Search name or email"
                    disabled={savingCurrent || userSearchLoading}
                  />
                  <button
                    type="button"
                    className="btn-secondary shrink-0"
                    onClick={searchUsers}
                    disabled={savingCurrent || userSearchLoading}
                  >
                    {userSearchLoading ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {!isRoleScope ? (
            <div>
              <FilterSelect
                label="User"
                className="w-full"
                value={selectedUserId || ''}
                options={userSelectOptions}
                onChange={(nextValue) => setSelectedUserId(String(nextValue))}
                disabled={savingCurrent || userSearchLoading || users.length === 0}
              />
            </div>
          ) : (
            <div className="flex items-end">
              <p className="text-sm text-slate-500">
                New users assigned this role inherit these defaults automatically.
              </p>
            </div>
          )}

          <div className="flex items-end">
            <button
              type="button"
              className="btn-primary w-full lg:w-auto"
              onClick={handleSave}
              disabled={loading || userSearchLoading || savingCurrent || (isRoleScope ? !selectedRole : !selectedUser)}
            >
              {savingCurrent ? 'Saving...' : `Save ${scopeLabel}`}
            </button>
          </div>
        </div>

        {!isRoleScope ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(320px,1fr)_1fr]">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Mode</label>
              <div className="flex flex-wrap items-center gap-4 rounded-md border border-slate-200 px-3 py-2">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="radio"
                    name="user-feature-mode"
                    value="inherit"
                    checked={userMode === 'inherit'}
                    onChange={() => setUserMode('inherit')}
                    disabled={!hasSelectedUser || savingCurrent}
                  />
                  Inherit Role Defaults
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="radio"
                    name="user-feature-mode"
                    value="custom"
                    checked={userMode === 'custom'}
                    onChange={() => setUserMode('custom')}
                    disabled={!hasSelectedUser || savingCurrent}
                  />
                  Custom Access
                </label>
              </div>
            </div>
            <div className="flex items-end">
              <p className="text-sm text-slate-500">
                {userSearchPerformed
                  ? `${users.length} shown${userResultCount > users.length ? ` of ${userResultCount}` : ''}. Search again to narrow results.`
                  : 'Users are loaded only after search to keep database reads small.'}
              </p>
            </div>
          </div>
        ) : null}

        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          {isRoleScope ? (
            <>
              Scope: <span className="font-semibold text-slate-800">Role Defaults</span>
              {' '}| Role: <span className="font-semibold text-slate-800">{selectedRole?.name || '-'}</span>
              {' '}| Mode: <span className="font-semibold text-slate-800">{roleHasAll ? 'Global (all)' : 'Feature-based'}</span>
            </>
          ) : (
            <>
              Scope: <span className="font-semibold text-slate-800">User Override</span>
              {' '}| User: <span className="font-semibold text-slate-800">{selectedUser?.name || '-'}</span>
              {' '}| Role: <span className="font-semibold text-slate-800">{selectedUser ? getUserRoleLabel(selectedUser) : '-'}</span>
              {' '}| Mode: <span className="font-semibold text-slate-800">{userMode === 'inherit' ? 'Inherit Role Defaults' : (userHasAll ? 'Custom (all)' : 'Custom (feature-based)')}</span>
            </>
          )}
        </div>

        {canEditFeatureGrid ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  const full = Array.from(new Set(FEATURE_OPTIONS.map((item) => item.key)));
                  if (isRoleScope) setRoleActions(full);
                  else setUserActions(full);
                }}
                disabled={savingCurrent || activeHasAll}
              >
                Select All
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  if (isRoleScope) setRoleActions([]);
                  else setUserActions([]);
                }}
                disabled={savingCurrent}
              >
                Clear
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  if (isRoleScope) setRoleActions(['all']);
                  else setUserActions(['all']);
                }}
                disabled={savingCurrent}
              >
                Grant `all`
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              {FEATURE_OPTIONS.map((feature) => {
                const checked = activeActions.includes(feature.key) || activeHasAll;
                return (
                  <label
                    key={`${scope}-${feature.key}`}
                    className={`rounded-xl border p-3 transition ${
                      checked ? 'border-primary-300 bg-primary-50' : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCurrentAction(feature.key)}
                        disabled={savingCurrent || activeHasAll}
                        className="mt-1"
                      />
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{feature.label}</p>
                        <p className="mt-1 text-xs text-slate-500">{feature.description}</p>
                        <p className="mt-2 rounded-md bg-slate-100 px-2 py-1 font-mono text-[11px] text-slate-700">
                          {feature.key}
                        </p>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </>
        ) : (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {hasSelectedUser
              ? 'This user currently inherits feature access from role defaults. Switch mode to "Custom Access" to edit.'
              : 'Search and select a user before editing user-specific feature access.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeatureAccessManagement;
